import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const useCreateVoucherFormSchema = () => {
  const { t } = useTranslation("voucher", {
    keyPrefix: "modal.createVoucherModal.form.validations"
  });

  const schema = useMemo(
    () =>
      z
        .object({
          voucherCode: z.string().min(1, {
            message: t("voucherCodeRequired")
          }),
          customerEmail: z.string().email({
            message: t("customerEmailInvalid")
          }),
          productId: z.string().min(1, {
            message: t("productNameRequired")
          }),
          productPackageId: z.string().min(1, {
            message: t("productPackageRequired")
          }),
          productVariantId: z.string().min(1, {
            message: t("productVariantRequired")
          }),
          priceDiscount: z.number().min(1, {
            message: t("discountAmountRequired")
          }),
          percentageDiscount: z
            .number()
            .min(1, {
              message: t("discountPercentRequired")
            })
            .max(99, {
              message: t("discountPercentMax")
            }),
          validStartDate: z
            .date({
              required_error: t("validStartDateRequired"),
              invalid_type_error: t("validStartDateInvalid")
            })
            .refine((date) => date instanceof Date && !isNaN(date.getTime()), {
              message: t("validStartDateInvalid")
            }),
          validEndDate: z
            .date({
              required_error: t("validEndDateRequired"),
              invalid_type_error: t("validEndDateInvalid")
            })
            .refine((date) => date instanceof Date && !isNaN(date.getTime()), {
              message: t("validEndDateInvalid")
            }),
          status: z.enum(["active", "inactive"]).default("active")
        })
        .refine(
          ({ validStartDate, validEndDate }) => {
            const start = new Date(validStartDate);
            const end = new Date(validEndDate);

            // Normalize to midnight (00:00) for calendar-only comparison
            start.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);

            return end > start;
          },
          {
            message: t("validEndDateLessThanStartDate"),
            path: ["validEndDate"]
          }
        ),
    [t]
  );

  return schema;
};

export type CreateVoucherFormSchema = z.infer<
  ReturnType<typeof useCreateVoucherFormSchema>
>;
